<script setup>
/**
 * Shared whiteboard for a live telehealth session (#5040).
 *
 * Ephemeral by design: strokes live only in this component's memory. Nothing is
 * written to FHIR, to the API, or to storage, and closing the board (or unmounting
 * it) discards everything. There is deliberately no save path.
 *
 * Transport lives somewhere else. This component knows nothing about Daily, sockets
 * or the peer — it emits the strokes the local user draws and applies the strokes
 * handed to it through `applyRemote`/`applyClear`. Whoever mounts it wires those two
 * ends to the data channel.
 *
 * Coordinates are normalized 0..1 against the canvas box rather than sent in pixels:
 * the clinician's docked panel and the patient's phone have very different canvas
 * sizes, and normalizing is what makes the same stroke land in the same place on both.
 * It also makes a resize a pure re-scale — we just redraw from the stored points.
 *
 * Every mark on the board is a "stroke", whatever tool drew it:
 *
 *   { id, tool: 'pen'|'line'|'rect'|'ellipse'|'text', color, width,
 *     points: [[x, y], ...], text?: string, done: boolean }
 *
 * `tool` is the only thing that changes how the points are read, and a stroke that
 * arrives WITHOUT it is a pen stroke. That default is not politeness — the two sides
 * of a call can be running different builds, and a client that predates the tool field
 * must keep drawing against one that doesn't.
 *
 * The tools differ in how their points update, which is what the peer has to get right:
 *
 *  - pen: many points, emitted incrementally. While a stroke is in progress we emit
 *    roughly every 50ms carrying ONLY the points added since the last emission for that
 *    stroke id (APPEND semantics on the receiving end), then a final `done: true` on
 *    pointer-up. Sending the whole stroke each time would grow without bound and blow
 *    past the 4096-byte per-message limit of the data channel; chunked appends stay
 *    small and constant.
 *  - line/rect/ellipse: exactly two points, [start, end]. The end point moves under the
 *    pointer, so each message resends the whole (two-point) shape and the receiver
 *    REPLACES what it had. Appending would leave a trail of stale previews behind.
 *  - text: exactly one point — the top-left anchor — plus `text`, emitted once on commit.
 *    Also replace semantics, for the same reason and so a re-sent stroke is idempotent.
 *
 * Text is typed into a real <input> floated over the canvas rather than a canvas-drawn
 * caret: the browser already gives us selection, IME and mobile keyboards for free, and
 * only the committed string is ever painted.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
    // View-only when false: no pointer drawing, no tools, no text entry. Remote strokes
    // still render.
    canDraw: { type: Boolean, default: false },
    height: { type: [Number, String], default: 320 },
});

const emit = defineEmits(['stroke', 'clear']);

// Icons are the shape each tool draws, so the picker reads without labels in the
// cramped dock toolbar.
const TOOLS = [
    { value: 'pen', icon: 'pen', label: 'Freehand pen' },
    { value: 'line', icon: 'minus', label: 'Straight line' },
    { value: 'rect', icon: 'square', label: 'Rectangle' },
    { value: 'ellipse', icon: 'circle', label: 'Ellipse' },
    { value: 'text', icon: 'font', label: 'Text' },
];

// Inks chosen to stay legible on the white board. Their swatches sit on the dark
// session chrome, so each gets a light hairline border — otherwise the near-black
// one would disappear into the toolbar.
const COLORS = [
    { value: '#1f2937', label: 'Black ink' },
    { value: '#dc2626', label: 'Red ink' },
    { value: '#2563eb', label: 'Blue ink' },
    { value: '#16a34a', label: 'Green ink' },
    { value: '#f59e0b', label: 'Amber ink' },
];
const WIDTHS = [
    { value: 2, label: 'Thin line' },
    { value: 4, label: 'Medium line' },
    { value: 8, label: 'Thick line' },
];

// Cadence of in-progress emissions, and the biggest batch we will put in one message.
// 64 points of 4-decimal normalized pairs serializes to well under 2KB, leaving plenty
// of head room inside the 4096-byte data-channel limit; a longer backlog is split
// across several messages rather than truncated.
const FLUSH_MS = 50;
const MAX_POINTS_PER_MESSAGE = 64;

// Growth guards. A real visit produces tens of strokes of a few hundred points each,
// so these ceilings are orders of magnitude above plausible use. They exist only so a
// very long session, a jittering stylus, or a misbehaving peer cannot grow the board
// until the tab runs out of memory. Past the per-stroke cap further points are
// dropped; past the board cap the oldest stroke is discarded.
const MAX_POINTS_PER_STROKE = 5000;
const MAX_STROKES = 500;

// A shape is defined by its two drag corners and text by its single anchor, so anything
// past that is noise from a peer we do not trust to be well behaved.
const POINTS_PER_TOOL = { line: 2, rect: 2, ellipse: 2, text: 1 };

// A whole typed line has to fit in one data-channel message alongside its stroke
// envelope, and a caption longer than this is a document, not a whiteboard annotation.
const MAX_TEXT_LENGTH = 240;

// Text size rides the ink-width picker so there is no second control to explain: thin /
// medium / thick land at roughly 3% / 6% / 12% of the board height. It is a fraction of
// the CANVAS rather than a pixel size because the same stroke has to look the same on a
// 900px dialog and a phone — and because a resize must re-scale text with everything
// else. The floor keeps it readable on a very short board.
const TEXT_HEIGHT_RATIO = 0.015;
const MIN_FONT_PX = 10;
const TEXT_FONT_FAMILY = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

const canvasRef = ref(null);
const textInputRef = ref(null);
const tool = ref(TOOLS[0].value);
const inkColor = ref(COLORS[0].value);
const inkWidth = ref(WIDTHS[1].value);

// The caret being typed into, or null. Only ever holds one placement: committing or
// cancelling is what makes room for the next.
const textDraft = ref(null);

// Reactive mirror of the canvas height, purely so the floating text input can be sized
// with the same formula the canvas uses. The drawing paths read the plain `cssH` below.
const boardHeight = ref(1);

// Source of truth for everything on the board. Deliberately not reactive: nothing in
// the template reads it, and a ref would re-run reactivity on every captured point.
const strokes = [];

let ctx = null;
let cssW = 1;
let cssH = 1;
let resizeObserver = null;

// Local stroke in progress, how many of its points have been emitted, and the pointer
// that owns it.
let active = null;
let sentCount = 0;
let activePointerId = null;
let flushTimer = null;

// Set when a shape's end point moved since the last emission, so a held-still drag does
// not re-send an identical two-point message every tick.
let shapeDirty = false;

const surfaceHeight = computed(() => (typeof props.height === 'number' || /^\d+$/.test(String(props.height)) ? `${props.height}px` : String(props.height)));

const round4 = (n) => Math.round(n * 10000) / 10000;
const clamp01 = (n) => Math.min(1, Math.max(0, n));
const nextStrokeId = () => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const copyPoints = (points) => (Array.isArray(points) ? points.filter((p) => Array.isArray(p) && p.length >= 2).map((p) => [p[0], p[1]]) : []);
const toPixels = (point) => [point[0] * cssW, point[1] * cssH];

// The one place the missing-tool default lives, so no caller has to remember it.
const toolOf = (stroke) => stroke?.tool || 'pen';
const limitPoints = (kind, points) => points.slice(0, POINTS_PER_TOOL[kind] ?? MAX_POINTS_PER_STROKE);
const fontPx = (width, height) => Math.max(MIN_FONT_PX, width * TEXT_HEIGHT_RATIO * height);

/**
 * The wire/snapshot form of a stroke. `text` is attached only for the text tool so a
 * pen or shape message carries nothing it cannot use.
 */
function strokePayload(stroke, points, done) {
    const payload = {
        id: stroke.id,
        tool: toolOf(stroke),
        color: stroke.color,
        width: stroke.width,
        points: points.map((p) => [p[0], p[1]]),
        done: !!done,
    };
    if (toolOf(stroke) === 'text') payload.text = stroke.text ?? '';
    return payload;
}

// --- canvas plumbing -----------------------------------------------------------

function resize() {
    const canvas = canvasRef.value;
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    cssW = rect.width || canvas.clientWidth || 1;
    cssH = rect.height || canvas.clientHeight || 1;
    boardHeight.value = cssH;
    // Back the canvas at device resolution but keep drawing in CSS pixels, so lines
    // are not blurry on retina/mobile screens.
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(cssW * dpr));
    canvas.height = Math.max(1, Math.round(cssH * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    redrawAll();
}

function applyInk(stroke) {
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
}

/**
 * Stroke part of a path. `from` is the index of the first NEW point; drawing starts
 * one point earlier so an appended piece joins the existing ink seamlessly. Batches of
 * several points are smoothed with quadratic curves through their midpoints and always
 * finish with a straight line to the last point, which is exactly where the next batch
 * picks up — so incremental drawing never leaves a gap.
 */
function drawSegments(stroke, from) {
    if (!ctx) return;
    const pts = stroke.points;
    if (!pts.length) return;
    applyInk(stroke);
    ctx.beginPath();
    if (pts.length === 1) {
        // A tap that never moved: a zero-length line with a round cap renders as a dot.
        const [x, y] = toPixels(pts[0]);
        ctx.moveTo(x, y);
        ctx.lineTo(x, y);
        ctx.stroke();
        return;
    }
    const start = Math.max(1, from);
    const [startX, startY] = toPixels(pts[start - 1]);
    ctx.moveTo(startX, startY);
    for (let i = start; i < pts.length; i++) {
        const [cx, cy] = toPixels(pts[i]);
        if (i + 1 < pts.length) {
            const [nx, ny] = toPixels(pts[i + 1]);
            ctx.quadraticCurveTo(cx, cy, (cx + nx) / 2, (cy + ny) / 2);
        } else {
            ctx.lineTo(cx, cy);
        }
    }
    ctx.stroke();
}

/**
 * Line, rectangle and ellipse all come from the same drag box: points[0] is where the
 * pointer went down and points[1] is where it is now. Outlines only — a fill would hide
 * whatever the shape was drawn around, which is the whole point of circling something.
 */
function drawShape(stroke) {
    if (!ctx || stroke.points.length < 2) return;
    const [x1, y1] = toPixels(stroke.points[0]);
    const [x2, y2] = toPixels(stroke.points[1]);
    applyInk(stroke);
    ctx.beginPath();
    if (toolOf(stroke) === 'line') {
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
    } else if (toolOf(stroke) === 'rect') {
        // Negative width/height is legal and is what lets the box be dragged upwards.
        ctx.rect(x1, y1, x2 - x1, y2 - y1);
    } else {
        ctx.ellipse((x1 + x2) / 2, (y1 + y2) / 2, Math.abs(x2 - x1) / 2, Math.abs(y2 - y1) / 2, 0, 0, Math.PI * 2);
    }
    ctx.stroke();
}

function drawText(stroke) {
    if (!ctx || !stroke.text || !stroke.points.length) return;
    const [x, y] = toPixels(stroke.points[0]);
    ctx.fillStyle = stroke.color;
    ctx.font = `${fontPx(stroke.width, cssH)}px ${TEXT_FONT_FAMILY}`;
    // The stored point is the top-left corner, so the painted text lands exactly where
    // the caret was — a baseline anchor would drift as the font size changed.
    ctx.textBaseline = 'top';
    ctx.fillText(stroke.text, x, y);
}

function drawStroke(stroke) {
    const kind = toolOf(stroke);
    if (kind === 'pen') drawSegments(stroke, 1);
    else if (kind === 'text') drawText(stroke);
    else drawShape(stroke);
}

function redrawAll() {
    if (!ctx) return;
    ctx.clearRect(0, 0, cssW, cssH);
    strokes.forEach((s) => drawStroke(s));
}

// --- board state ---------------------------------------------------------------

function pushStroke(stroke) {
    strokes.push(stroke);
    while (strokes.length > MAX_STROKES) strokes.shift();
    return stroke;
}

function appendPoint(stroke, point) {
    if (stroke.points.length >= MAX_POINTS_PER_STROKE) return false;
    stroke.points.push(point);
    return true;
}

function resetBoard() {
    strokes.length = 0;
    active = null;
    sentCount = 0;
    activePointerId = null;
    shapeDirty = false;
    textDraft.value = null;
    stopFlushTimer();
    redrawAll();
}

// --- emission ------------------------------------------------------------------

/**
 * Emit what has changed for the stroke in progress. `done` marks the stroke finished —
 * that final message is always emitted, even when there is nothing new left to send, so
 * the peer knows the stroke is closed.
 */
function flush(done = false) {
    if (!active) return;
    if (toolOf(active) !== 'pen') {
        // A shape is two points total: nothing to chunk, and the peer replaces rather
        // than appends, so re-sending the pair is both the update and the whole truth.
        if (!done && !shapeDirty) return;
        shapeDirty = false;
        emit('stroke', strokePayload(active, active.points, done));
        return;
    }
    if (!done && sentCount >= active.points.length) return;
    do {
        const chunk = active.points.slice(sentCount, sentCount + MAX_POINTS_PER_MESSAGE);
        sentCount += chunk.length;
        emit('stroke', strokePayload(active, chunk, done && sentCount >= active.points.length));
    } while (sentCount < active.points.length);
}

function startFlushTimer() {
    stopFlushTimer();
    flushTimer = setInterval(() => flush(false), FLUSH_MS);
}

function stopFlushTimer() {
    if (flushTimer === null) return;
    clearInterval(flushTimer);
    flushTimer = null;
}

// --- pointer input -------------------------------------------------------------

function toNormalized(event) {
    const canvas = canvasRef.value;
    if (!canvas || !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return null;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width || cssW || 1;
    const h = rect.height || cssH || 1;
    // Clamped because pointer capture keeps delivering moves after the pointer leaves
    // the canvas, and out-of-range coordinates would land off-board on the peer.
    return [round4(clamp01((event.clientX - rect.left) / w)), round4(clamp01((event.clientY - rect.top) / h))];
}

function onPointerDown(event) {
    if (!props.canDraw || active) return;
    const point = toNormalized(event);
    if (!point) return;
    // Pressing anywhere else on the board is "clicking away" from an open caret, so it
    // commits first and the press then does whatever the current tool does.
    if (textDraft.value) commitText();
    if (tool.value === 'text') {
        // Stop the browser's default pointerdown focus handling. The canvas is not
        // focusable, so that default moves focus to the body — which blurs the caret
        // we are about to focus, and blur commits it while it is still empty. The
        // caret would appear and vanish in the same frame. jsdom does not model
        // default focus behaviour, which is why the unit tests never caught this.
        event.preventDefault();
        openTextDraft(point);
        return;
    }
    // A shape starts as a degenerate box at the press point; the second point is what
    // the drag moves, which is why it is seeded rather than appended later.
    const points = tool.value === 'pen' ? [point] : [point, point];
    active = pushStroke({ id: nextStrokeId(), tool: tool.value, color: inkColor.value, width: inkWidth.value, points, done: false });
    sentCount = 0;
    shapeDirty = false;
    activePointerId = event.pointerId;
    event.currentTarget?.setPointerCapture?.(event.pointerId);
    if (toolOf(active) === 'pen') drawSegments(active, 0);
    startFlushTimer();
}

function onPointerMove(event) {
    if (!active || event.pointerId !== activePointerId) return;
    const point = toNormalized(event);
    if (!point) return;
    if (toolOf(active) !== 'pen') {
        const end = active.points[1];
        if (point[0] === end[0] && point[1] === end[1]) return;
        active.points[1] = point;
        shapeDirty = true;
        // A shape has no new ink to add — the previous preview has to be erased — so the
        // live preview costs a full repaint on every move. Boards hold tens of strokes,
        // not thousands, so that is cheaper than keeping a second buffer around.
        redrawAll();
        return;
    }
    const last = active.points[active.points.length - 1];
    if (last && point[0] === last[0] && point[1] === last[1]) return;
    if (!appendPoint(active, point)) return;
    drawSegments(active, active.points.length - 1);
}

// Idempotent: pointer-up reaches us both from the canvas and from the window safety
// net below, and a cancelled pointer closes the stroke the same way.
function endStroke(event) {
    if (!active) return;
    flush(true);
    stopFlushTimer();
    active.done = true;
    active = null;
    sentCount = 0;
    shapeDirty = false;
    if (event && activePointerId !== null && activePointerId !== undefined) event.currentTarget?.releasePointerCapture?.(activePointerId);
    activePointerId = null;
}

// Safety net for a pointer released outside the canvas when pointer capture is
// unavailable — without it the stroke would never be closed for the peer.
function onWindowPointerEnd() {
    endStroke();
}

// --- text entry ------------------------------------------------------------------

function openTextDraft(point) {
    textDraft.value = { point, text: '' };
    // The input only exists once the draft renders, so focus has to wait for it.
    nextTick(() => textInputRef.value?.focus());
}

/**
 * Turn the caret into a stroke. A caret left empty is a mis-click, not a mark: nothing
 * is stored and nothing is emitted, so the peer never sees a phantom stroke.
 */
function commitText() {
    const draft = textDraft.value;
    textDraft.value = null;
    if (!draft) return;
    const text = String(draft.text || '')
        .trim()
        .slice(0, MAX_TEXT_LENGTH);
    if (!text) return;
    // Copied out of the draft: the draft object is reactive, and the board deliberately
    // is not.
    const anchor = [draft.point[0], draft.point[1]];
    const stroke = pushStroke({ id: nextStrokeId(), tool: 'text', color: inkColor.value, width: inkWidth.value, points: [anchor], text, done: true });
    drawStroke(stroke);
    emit('stroke', strokePayload(stroke, stroke.points, true));
}

function cancelText() {
    textDraft.value = null;
}

// --- tools ---------------------------------------------------------------------

function selectTool(value) {
    // Switching tools with a caret open would strand it, so it commits like any other
    // click away from the text.
    if (textDraft.value) commitText();
    tool.value = value;
}

function onClearClick() {
    resetBoard();
    emit('clear');
}

// --- peer-driven API (never emits — otherwise the two sides would echo forever) ---

function applyRemote(stroke) {
    if (!stroke || !stroke.id) return;
    const kind = toolOf(stroke);
    const points = limitPoints(kind, copyPoints(stroke.points));
    const existing = strokes.find((s) => s.id === stroke.id);
    if (existing) {
        if (toolOf(existing) === 'pen') {
            const from = existing.points.length;
            points.forEach((p) => appendPoint(existing, p));
            if (stroke.done) existing.done = true;
            if (existing.points.length > from) drawSegments(existing, from);
            return;
        }
        // Shapes and text resend their whole definition every time: those points are the
        // CURRENT drag box, not new ink, so appending them would pile stale previews on
        // top of each other. Replacing means the last message wins, and a duplicate
        // message is harmless.
        existing.points = points;
        if (kind === 'text' && typeof stroke.text === 'string') existing.text = stroke.text.slice(0, MAX_TEXT_LENGTH);
        if (stroke.done) existing.done = true;
        redrawAll();
        return;
    }
    const added = pushStroke({
        id: stroke.id,
        tool: kind,
        color: stroke.color || COLORS[0].value,
        width: stroke.width || WIDTHS[1].value,
        points,
        ...(kind === 'text' ? { text: String(stroke.text ?? '').slice(0, MAX_TEXT_LENGTH) } : {}),
        done: !!stroke.done,
    });
    drawStroke(added);
}

function applyClear() {
    resetBoard();
}

function snapshot() {
    return strokes.map((s) => strokePayload(s, s.points, s.done));
}

function restore(list) {
    resetBoard();
    (Array.isArray(list) ? list : []).forEach((s) => {
        if (!s || !s.id) return;
        const kind = toolOf(s);
        pushStroke({
            id: s.id,
            tool: kind,
            color: s.color || COLORS[0].value,
            width: s.width || WIDTHS[1].value,
            points: limitPoints(kind, copyPoints(s.points)),
            ...(kind === 'text' ? { text: String(s.text ?? '').slice(0, MAX_TEXT_LENGTH) } : {}),
            done: !!s.done,
        });
    });
    redrawAll();
}

defineExpose({ applyRemote, applyClear, snapshot, restore });

// --- lifecycle -----------------------------------------------------------------

onMounted(() => {
    ctx = canvasRef.value?.getContext('2d') || null;
    resize();
    if (typeof ResizeObserver !== 'undefined' && canvasRef.value) {
        resizeObserver = new ResizeObserver(() => resize());
        resizeObserver.observe(canvasRef.value);
    }
    window.addEventListener('pointerup', onWindowPointerEnd);
    window.addEventListener('pointercancel', onWindowPointerEnd);
});

onBeforeUnmount(() => {
    window.removeEventListener('pointerup', onWindowPointerEnd);
    window.removeEventListener('pointercancel', onWindowPointerEnd);
    stopFlushTimer();
    resizeObserver?.disconnect();
    resizeObserver = null;
    ctx = null;
    // Nothing is persisted — the board dies with the component.
    textDraft.value = null;
    strokes.length = 0;
});
</script>

<template>
    <div class="bn-wb d-flex flex-column">
        <div v-if="canDraw" class="bn-wb-tools d-flex align-center flex-wrap ga-1 px-2 py-1">
            <v-btn
                v-for="t in TOOLS"
                :key="t.value"
                icon
                size="x-small"
                :variant="t.value === tool ? 'tonal' : 'text'"
                :title="t.label"
                :aria-label="t.label"
                data-cy="thWbTool"
                @click="selectTool(t.value)"
            >
                <font-awesome-icon :icon="['far', t.icon]" />
            </v-btn>
            <v-divider vertical class="mx-1" />
            <v-btn
                v-for="c in COLORS"
                :key="c.value"
                icon
                size="x-small"
                variant="flat"
                class="bn-wb-swatch"
                :class="{ 'bn-wb-swatch--on': c.value === inkColor }"
                :style="{ backgroundColor: c.value }"
                :title="c.label"
                :aria-label="c.label"
                data-cy="thWbColor"
                @click="inkColor = c.value"
            />
            <v-divider vertical class="mx-1" />
            <v-btn
                v-for="w in WIDTHS"
                :key="w.value"
                icon
                size="x-small"
                :variant="w.value === inkWidth ? 'tonal' : 'text'"
                :title="w.label"
                :aria-label="w.label"
                data-cy="thWbWidth"
                @click="inkWidth = w.value"
            >
                <span class="bn-wb-dot" :style="{ width: `${w.value}px`, height: `${w.value}px` }" />
            </v-btn>
            <v-spacer />
            <v-btn size="x-small" variant="text" color="error" title="Clear the board" data-cy="thWbClear" @click="onClearClick">
                <font-awesome-icon :icon="['far', 'trash']" class="mr-1" />Clear
            </v-btn>
        </div>

        <div class="bn-wb-surface" :style="{ height: surfaceHeight }">
            <canvas
                ref="canvasRef"
                class="bn-wb-canvas"
                :class="{ 'bn-wb-canvas--view': !canDraw, 'bn-wb-canvas--text': canDraw && tool === 'text' }"
                data-cy="thWhiteboard"
                @pointerdown="onPointerDown"
                @pointermove="onPointerMove"
                @pointerup="endStroke"
                @pointercancel="endStroke"
            ></canvas>

            <!-- Positioned in percentages so the caret keeps its place across a resize,
                 exactly like the normalized point it will be committed as. -->
            <input
                v-if="canDraw && textDraft"
                ref="textInputRef"
                v-model="textDraft.text"
                type="text"
                class="bn-wb-text-input"
                :maxlength="MAX_TEXT_LENGTH"
                :style="{
                    left: `${textDraft.point[0] * 100}%`,
                    top: `${textDraft.point[1] * 100}%`,
                    color: inkColor,
                    fontSize: `${fontPx(inkWidth, boardHeight)}px`,
                }"
                placeholder="Type, then press enter"
                aria-label="Whiteboard text"
                data-cy="thWbText"
                @keydown.enter.prevent="commitText"
                @keydown.esc.prevent="cancelText"
                @blur="commitText"
            />
        </div>
    </div>
</template>

<style scoped>
/* The board is deliberately white while the session chrome around it is dark: people
   read a whiteboard as white, dark ink on a light sheet is the legible combination for
   handwriting, and the hard contrast makes the drawing area obviously separate from
   the video panel it sits in. */
.bn-wb-surface {
    position: relative;
    width: 100%;
    /* A floor so a height that resolves to nothing (a percentage against an
       auto-height parent, say) degrades to a small board instead of collapsing
       to zero — which clips the canvas and the text caret away silently. */
    min-height: 120px;
    background: #fff;
    border-radius: 6px;
    overflow: hidden;
}
.bn-wb-canvas {
    display: block;
    width: 100%;
    height: 100%;
    /* Required: without it a touch drag scrolls the page instead of drawing. */
    touch-action: none;
    cursor: crosshair;
}
.bn-wb-canvas--view {
    cursor: default;
}
.bn-wb-canvas--text {
    cursor: text;
}
/* The caret is a borderless field in the ink colour and the committed text size, so what
   is typed already looks like what will be painted; the dashed underline is the only
   hint that it is still editable. */
.bn-wb-text-input {
    position: absolute;
    min-width: 6rem;
    max-width: 90%;
    padding: 0;
    background: transparent;
    border: 0;
    border-bottom: 1px dashed rgba(0, 0, 0, 0.35);
    outline: none;
    line-height: 1.1;
    font-family:
        system-ui,
        -apple-system,
        'Segoe UI',
        Roboto,
        sans-serif;
}
.bn-wb-tools {
    background: #262626;
    color: #fff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
/* Hairline so the near-black swatch is still visible against the dark toolbar. */
.bn-wb-swatch {
    border: 1px solid rgba(255, 255, 255, 0.45);
}
.bn-wb-swatch--on {
    outline: 2px solid #fff;
    outline-offset: 2px;
}
.bn-wb-dot {
    display: block;
    border-radius: 50%;
    background: currentcolor;
}
</style>
